import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import {ReactComponent as EditIcon} from 'assets/icons/editIcon.svg';
import {ReactComponent as DeleteIcon} from 'assets/icons/delete.svg';
import Button from 'components/common/Button';
import withConfirm from 'hooks/withConfirm';
import { useMutation } from '@apollo/react-hooks';
import { GET_ESSAY_PROMPTS, DELETE_PROMPT } from 'graphql/essays';
import useSnack from 'hooks/useSnack';
import AddPromptModal from './AddPromptModal';
import { useSelector } from 'react-redux';
import { Draggable, Droppable } from 'react-beautiful-dnd';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(4),
  },
  topicLabel: {
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    fontWeight: 700,
    fontSize: 10,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  topicBox: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 6,
    padding: theme.spacing(1),
    flexGrow: 1,
  },
  topicText: {
    fontSize: 13,
    flexGrow: 1,
    display: 'block'
  },
  actionBtn: {
    padding: theme.spacing(1),
    marginTop:theme.spacing(1)
  
  },
  divider: {
    marginTop: theme.spacing(4),
  },
  title: {
    [theme.breakpoints.down('sm')]: {
      ...theme.typography.caption,
    },
    marginTop: theme.spacing(1)
  }
}));

const TopicItem = ({ label, text }) => {
  const classes = useStyles();

  return (
    <Box marginRight={3} flexGrow={1} width={{ xs: '100%', sm: '30%' }} display="flex" flexDirection="column">
      <Typography  className={classes.topicLabel}>{label}</Typography>
      <Box className={classes.topicBox}>
        <Typography variant="caption"  className={classes.topicText}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
};

const PromptsList = ({ confirm, student, category, prompts, index }) => {
  const classes = useStyles();
  const user = useSelector((state) => state.user.user);
  const [showModal, toggleModal] = useState(false);
  const [selected, setSelected] = useState();
  const [deletePrompt] = useMutation(DELETE_PROMPT);
  const snack = useSnack();
  const isStudent = user.type === 'student';
  const canAdd = ['student', 'lead_coach', 'head_coach', 'admin'].includes(
    user.type,
  );

  const setEditing = (row) => {
    setSelected(row);
    toggleModal(true);
  };

  const closeModal = () => {
    setSelected();
    toggleModal(false);
  };

  const onDelete = (row) => {
    confirm(
      () => {
        return deletePrompt({
          variables: { id: row.id },
          update: (proxy) => {
            const { essay_prompts } = proxy.readQuery({
              query: GET_ESSAY_PROMPTS,
              variables: { userId: row.user_id },
            });
            proxy.writeQuery({
              query: GET_ESSAY_PROMPTS,
              variables: { userId: row.user_id },
              data: {
                essay_prompts: essay_prompts.filter((i) => i.id !== row.id),
              },
            });
          },
        }).then(
          () => snack.success('Prompt deleted'),
          (e) => snack.error(e.message),
        );
      },
      {
        title: 'Remove Topic',
        description: 'Do you want to remove the topic?',
        confirmationText: 'Remove topic',
        confirmButtonProps: {
          variant: 'contained',
          color: 'secondary',
        },
      },
    );
  };

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography paragraph variant="subtitle1" className={classes.title}>
          {category.text}
        </Typography>
        <Divider />
        <Droppable droppableId={String(category.id)}>
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              <Box>
                {prompts
                  .filter((p) => p.category_id === category.id)
                  .map((row) => (
                    <>
                      <Draggable
                        draggableId={String(row.id)}
                        index={index}
                        isDragDisabled={!canAdd}
                      >
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Box display="flex" marginTop={2} marginBottom={2} flexDirection={{ xs: 'column', sm: 'row'}}>
                              <TopicItem label="Situation" text={row.situation} />
                              <TopicItem label="Task" text={row.task} />
                              <TopicItem label="Action" text={row.action} />
                              <TopicItem label="Result" text={row.result} />
                              {canAdd && (
                                <Box display="flex" flexDirection={{xs: 'row', sm: 'column'}} paddingTop={1}>
                                  <IconButton onClick={() => setEditing(row)} className={classes.actionBtn}>
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton onClick={() => onDelete(row)} className={classes.actionBtn}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                      <Divider className={classes.divider} />
                    </>
                  ))}
              </Box>
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
        {canAdd && (
          <Box marginTop={5} textAlign="center">

            <Button
              size="large"
              color="primary"
              variant="outlined"
              onClick={() => toggleModal(true)}
            >
              Add New Topic
            </Button>
          </Box>
        )}
      </CardContent>

      {showModal && (
        <AddPromptModal
          student={student}
          category={category}
          onClose={() => closeModal()}
          onEdit={setEditing}
          prompts={prompts}
          prompt={selected}
        />
      )}
    </Card>
  );
  // )
};

export default withConfirm(PromptsList);
